import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { hrmModules, HrmModuleFeature } from "../hrmModulesData";

const FeatureBlock: React.FC<{ feature: HrmModuleFeature }> = ({ feature }) => (
  <div className="hms-module-feature">
    <h4 className="hms-module-feature__title">{feature.title}</h4>
    {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
    {feature.items && (
      <ul className="hms-module-feature__list">
        {feature.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )}
  </div>
);

const HrmModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(hrmModules[0].id);
  const activeModule = hrmModules.find((m) => m.id === activeId) ?? hrmModules[0];

  return (
    <section className="hms-modules hrm-modules" aria-labelledby="hrm-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="hrm-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            Module Features
          </h2>
          <p className="hms-modules__lead">
            Dedicated modules for employee management, attendance, payroll, recruitment, performance,
            and reporting — each built for specific HR workflows.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs" role="tablist" aria-label="HRM modules">
          {hrmModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`hrm-panel-${mod.id}`}
              id={`hrm-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {mod.title.replace(" Management", "").replace(" & Analytics", "")}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`hrm-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`hrm-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HrmModulesSection;
